import { CRAYO_ENTRY, crayoPlan, formatHours, money, crayoPlanTerms } from "@/lib/compare/crayo";
import type { CompareContent } from "@/lib/compare/types";

const hobby = CRAYO_ENTRY;
const clipper = crayoPlan("Clipper");
const pro = crayoPlan("Pro");

const THEIR_PAGE = "https://autoshorts.ai/alternative/crayo-ai";
const THEIR_PAGE_DATE = "2025-11-12";

export const AUTOSHORTS_CONTENT: CompareContent = {
  slug: "crayo-vs-autoshorts",
  competitorSlug: "autoshorts",
  title: "Crayo vs AutoShorts",
  seoTitle: "Crayo vs AutoShorts: Clipping Editor vs Faceless Series Generator",
  seoDescription:
    "Crayo vs AutoShorts, dated prices. AutoShorts sells one auto-posting series per plan, 90-second videos, no refunds, no clipping. Crayo clips long video into ranked shorts with an editor on every plan.",
  cardVerdict: "Better than Crayo for auto-posting and voice cloning, behind on clipping real footage, editing and volume.",
  bestFor: "Clippers weighing an editor against a hands-off generator",
  datePublished: "2026-10-09",
  dateModified: "2026-10-25",
  verdict:
    `As of October 25, 2026, Crayo is the better pick if you clip real footage: AutoClip ranks the moments in a podcast or stream, reframes faces and opens every clip in a timeline editor, with ${hobby.credits} credits for ${money(hobby.monthly)} a month. AutoShorts is the better pick if you want one faceless series written, rendered and posted to TikTok, YouTube and Instagram without touching it. The evidence is below.`,
  pickCrayoIf: [
    "You clip podcasts, streams or interviews: Crayo's AutoClip takes a source up to 3 hours and returns 1 to 20 ranked clips with a face-aware camera; AutoShorts has no upload-and-clip product and its FAQ says no long form.",
    `You keep a few clips from each long source and want to pay per clip: one Crayo credit is one finished clip and Hobby carries ${hobby.credits} for ${money(hobby.monthly)} a month, while AutoShorts caps every plan at 1 series and its FAQ says a second niche needs a separate account.`,
    "You want to edit the result: Crayo includes a timeline editor, 32 caption styles in 56 spoken languages and a public API on every plan; AutoShorts edits are limited to script, title, images and music swap.",
  ],
  pickThemIf: [
    "You want a channel that runs itself. AutoShorts posts to TikTok, YouTube and Instagram on a schedule; Crayo has no scheduler and you post each file yourself.",
    "You want your own voice on every video. AutoShorts lists voice cloning on every plan card; Crayo has hundreds of voices but no cloning.",
    "You want the whole series generated in one of 30 languages. AutoShorts lists 30 on its FAQ; Crayo publishes no script-language count for its generators.",
  ],
  wins: [
    {
      claim: "Crayo clips real footage into ranked shorts; AutoShorts has no upload-and-clip product.",
      evidence:
        `The AutoShorts FAQ says the product does not focus on individual video creation and answers "Can this make long form content?" with "Not at the moment." Crayo's AutoClip takes a YouTube or TikTok link or a file up to 3 hours, ranks the moments, and cuts 1 to 20 face-framed clips of 15 to 120 seconds, checked Oct 9, 2026.`,
      href: "https://autoshorts.ai/faq",
    },
    {
      claim: "Crayo includes a timeline editor on every plan; AutoShorts has no editor.",
      evidence:
        `The AutoShorts homepage lists editing as script, title, images and music swap, and its FAQ offers 30 to 60 or 60 to 90 second videos from scripts capped at 1,600 characters. Crayo's editor with camera track, keyframes, zooms, speed ramps and 32 caption styles opens every clip on every plan, and Clip Cutter trims any range by hand for free.`,
      href: "https://crayo.ai/features/auto-clip",
    },
    {
      claim: "Crayo meters per finished clip; AutoShorts sells a posting cadence locked to one series.",
      evidence:
        `AutoShorts Starter is $19 a month for one series that posts 3 times a week, about 13 videos, per its homepage on Oct 9, 2026, and the FAQ says a second niche means a second account. Crayo Hobby is ${money(hobby.monthly)} a month or ${money(hobby.yearlyTotal)} a year for ${hobby.credits} workflow credits, each one a finished clip, across as many sources and projects as you like.`,
      href: "https://autoshorts.ai",
    },
    {
      claim: "Crayo publishes a 34-endpoint API and View Tracker; AutoShorts publishes no API and no analytics.",
      evidence:
        "No API or analytics appear on autoshorts.ai or its FAQ as of Oct 8, 2026. Crayo's API covers autoclip, exports, voiceover, images and webhooks on every plan, documented at crayo.ai/developers.",
      href: "https://crayo.ai/developers",
    },
    {
      claim: "Crayo's free browser tools work today; the AutoShorts free plan is paused.",
      evidence:
        'The AutoShorts homepage marks the $0 plan "Temporarily paused" and its FAQ says the free plan was suspended "due to abuse from botting / spamming", checked Oct 9, 2026. Crayo has no free plan either, but twelve browser tools at crayo.ai/features/free need no account.',
      href: "https://crayo.ai/features/free",
    },
  ],
  concessions: [
    {
      claim: "AutoShorts posts for you; Crayo does not.",
      evidence:
        'The AutoShorts FAQ says "We currently support posting to TikTok, YouTube, and Instagram", and every paid plan is defined by its posting cadence. Crayo exports a file and has no scheduler, which the AutoShorts page about Crayo correctly points out.',
      href: "https://autoshorts.ai/faq",
    },
    {
      claim: "AutoShorts is genuinely hands-off; Crayo needs you in the loop.",
      evidence:
        "An AutoShorts series takes a topic, art style, voice, language and duration once, then writes, renders and posts on its own. Crayo starts from your link or upload each time and you review the clips before export.",
      href: "https://autoshorts.ai",
    },
    {
      claim: "AutoShorts includes voice cloning on every plan.",
      evidence:
        "Voice Cloning appears on the Starter, Daily and Hardcore cards on the homepage, checked Oct 9, 2026. Crayo offers hundreds of stock voices and a voice changer, but no cloning.",
      href: "https://autoshorts.ai",
    },
    {
      claim: "AutoShorts lists 30 script and voice languages.",
      evidence:
        "The FAQ lists 30 languages from Arabic to Ukrainian for generated series. Crayo transcribes 56 spoken languages for captions and publishes no script-language count for its generators.",
      href: "https://autoshorts.ai/faq",
    },
  ],
  typicalMonth: {
    scenario: "One 60-minute source a week, eight clips each: 240 source minutes and 32 clips a month.",
    crayo: {
      plan: "Hobby",
      cost: `${money(hobby.monthly)}/mo or about ${money(hobby.yearlyPerMonth)}/mo yearly`,
      usage: `32 of ${hobby.credits} workflow credits and about 32 of ${hobby.exportMinutes} export minutes`,
      note: "Prices from the live plan table. You post the 32 clips yourself.",
    },
    them: {
      plan: "No equivalent",
      cost: "n/a",
      usage:
        "AutoShorts has no upload-and-clip product: its FAQ says no long form and scripts stop at 1,600 characters, so the 240 source minutes cannot go in. Starter at $19 a month instead generates and posts about 13 faceless videos from one series.",
      note: "Cadence and prices from the AutoShorts homepage, Oct 9, 2026; FAQ facts checked Oct 8, 2026.",
    },
  },
  test: {
    status: "scheduled",
    source:
      "A 20-minute single-speaker talking-head video owned by Crayo, with the same written topic brief given to both tools",
    plans: "AutoShorts Starter and Crayo Clipper, same file where accepted, same day, defaults first then one tuned run each",
  },
  billingNote:
    "AutoShorts has no refunds and no annual total printed on its cards; yearly billing is shown per month under a toggle labelled 2 MONTHS FREE. Motion credits reset each cycle, image credits refill on a 30-day rolling basis, and purchased top-off credits never expire.",
  corrections: [
    {
      site: "AutoShorts",
      url: THEIR_PAGE,
      date: THEIR_PAGE_DATE,
      claim: "Hobby Plan: At $19 per month, it includes 40 minutes of video export.",
      reality: `As of October 25, 2026, Crayo Hobby is ${crayoPlanTerms(hobby)} and includes ${formatHours(hobby.exportMinutes)} of export, ${hobby.credits} workflow credits, ${hobby.voiceoverMinutes} voiceover minutes and ${hobby.images} AI images, per the live pricing page.`,
      evidenceHref: "https://crayo.ai/pricing",
      evidenceLabel: "Crayo pricing",
    },
    {
      site: "AutoShorts",
      url: THEIR_PAGE,
      date: THEIR_PAGE_DATE,
      claim: "Clipper Plan: At $39 per month, it provides 2 hours of video export with 30 avatar minutes.",
      reality: `As of October 25, 2026, Crayo Clipper is ${crayoPlanTerms(clipper)} and includes ${formatHours(clipper.exportMinutes)} of export and ${clipper.credits} workflow credits. "Avatar minutes" refers to an AI avatar feature Crayo removed in 2025; no plan includes avatar minutes today.`,
      evidenceHref: "https://crayo.ai/pricing",
      evidenceLabel: "Crayo pricing",
    },
    {
      site: "AutoShorts",
      url: THEIR_PAGE,
      date: THEIR_PAGE_DATE,
      claim: "Pro Plan: At $79 per month, it offers 3 hours of video export and 500 AI images.",
      reality: `As of October 25, 2026, Crayo Pro is ${crayoPlanTerms(pro)} and includes ${formatHours(pro.exportMinutes)} of export, ${pro.credits} workflow credits and ${pro.images} AI images.`,
      evidenceHref: "https://crayo.ai/pricing",
      evidenceLabel: "Crayo pricing",
    },
    {
      site: "AutoShorts",
      url: THEIR_PAGE,
      date: THEIR_PAGE_DATE,
      claim: "Crayo AI focuses on video creation rather than distribution.",
      reality:
        "Correct and still current. Crayo has no scheduler and no auto-posting; you download the clip and post it. View Tracker follows other creators' cadence and outliers and is not a scheduler.",
      evidenceHref: "https://crayo.ai/features/auto-clip",
      evidenceLabel: "AutoClip",
    },
    {
      site: "AutoShorts",
      url: THEIR_PAGE,
      date: THEIR_PAGE_DATE,
      claim: 'The page ends with the button "Try AutoShorts.ai for Free (No credit card required)".',
      reality:
        'On the same site, the homepage pricing card marks the Free plan "Temporarily paused" and the FAQ says the free plan is suspended "due to abuse from botting / spamming", both checked Oct 9, 2026.',
      evidenceHref: "https://autoshorts.ai",
      evidenceLabel: "AutoShorts homepage",
    },
  ],
  switchingCancelNote:
    "The AutoShorts FAQ (autoshorts.ai/faq) says you can cancel in one click from the billing page, and that refunds are not offered.",
  faqs: [
    {
      question: "Is Crayo cheaper than AutoShorts for clipping?",
      answer:
        `For clipping there is nothing to price against: AutoShorts has no upload-and-clip product. On volume, Crayo Hobby is ${crayoPlanTerms(hobby)} for ${hobby.credits} workflow credits, one per finished clip, with ${formatHours(hobby.exportMinutes)} of export. AutoShorts Starter is $19 a month, or $16 a month billed yearly, for one series that posts 3 times a week, about 13 videos, so Starter is the lower sticker on monthly billing and the two are close on quarterly and yearly billing; thirty videos a month needs Daily at $39 a month ($33 yearly). The price AutoShorts includes that Crayo does not is the posting: it publishes those videos to TikTok, YouTube and Instagram for you.`,
    },
    {
      question: "Can AutoShorts clip a podcast or a stream?",
      answer:
        `No. AutoShorts generates every video from a topic brief and has no upload. Its FAQ says the product does not focus on individual video creation, offers 30 to 60 or 60 to 90 second videos from scripts of up to 1,600 characters, and answers "Can this make long form content?" with "Not at the moment." Crayo's AutoClip takes a YouTube or TikTok link or a file from 1 minute to 3 hours, Find Viral Moments lists up to 50 ranked moments before a credit is spent, and each run cuts 1 to 20 clips of 15 to 120 seconds with a face-aware camera and captions in 56 spoken languages.`,
    },
    {
      question: "Does AutoShorts have a free plan?",
      answer:
        `Not at the moment. The AutoShorts homepage lists a $0 plan for 1 video and 1 series, but the card is marked "Temporarily paused" and the FAQ says the free plan was suspended "due to abuse from botting / spamming", both checked Oct 9, 2026. Its page about Crayo still ends with "Try AutoShorts.ai for Free". Crayo has no free plan and no trial either; the entry plan is Hobby at ${money(hobby.monthly)} a month, and twelve free browser tools at crayo.ai/features/free need no account.`,
    },
    {
      question: "Does Crayo auto-post like AutoShorts?",
      answer:
        'No. Crayo exports a file and you post it yourself. AutoShorts posts on a schedule, and its FAQ says "We currently support posting to TikTok, YouTube, and Instagram." The same FAQ carries a warning worth reading first: "If your videos are getting very few or even zero views, it\'s likely that the platform\'s algorithm thinks your account is a bot." If hands-off posting is the deciding feature, AutoShorts wins that row and Crayo does not compete on it.',
    },
    {
      question: "What does the one series limit on AutoShorts mean?",
      answer:
        "Every AutoShorts plan, from Starter to Hardcore, is capped at 1 series, which is one topic with one art style, voice, language and duration. The FAQ says an account can only be on one plan type at a time and that the workaround for a second niche is a separate account, so two niches means two subscriptions. Crayo has no equivalent cap: credits spend on any clip from any source, so a podcast channel and a stream-highlights channel share one Hobby plan, and the story and text video generators included on every plan draw from the same pool.",
    },
    {
      question: "Does AutoShorts offer refunds? Does Crayo?",
      answer:
        'Neither does. The AutoShorts FAQ says "we cannot offer refunds as costs incurred for creating AI videos... are extremely high" and that "During sign up you agree to withhold your right to refund." Crayo\'s policy is the same in substance: no refunds, all sales final. Both let you cancel yourself; AutoShorts from the billing page in one click per its FAQ, Crayo in one click from Settings with the plan active to the end of the cycle.',
    },
    {
      question: "Does Crayo have voice cloning like AutoShorts?",
      answer:
        "No. AutoShorts lists Voice Cloning on every paid plan card as of Oct 9, 2026. Crayo offers hundreds of stock voices for voiceovers, plus a voice changer and Enhance Speech for cleaning up a clip's audio, but it cannot clone your voice. If narrating a generated series in your own voice is the point of the channel, AutoShorts wins that row.",
    },
    {
      question: "Is what AutoShorts says about Crayo's pricing accurate?",
      answer:
        `Partly. The AutoShorts page about Crayo, dated November 12, 2025, quotes the Hobby monthly price Crayo charged until October 2026, with 40 minutes of export, and Clipper with 30 avatar minutes. As of October 25, 2026, Hobby is ${crayoPlanTerms(hobby)} with ${formatHours(hobby.exportMinutes)} of export, and no plan has avatar minutes because that feature was removed in 2025. Its claim that Crayo focuses on creation rather than distribution is correct. The corrections box above answers each line with the current figure.`,
    },
  ],
  changelog: [
    {
      date: "2026-10-09",
      text: "First published. AutoShorts prices checked Oct 9, 2026: Starter $19/mo or $16/mo billed yearly, Daily $39/mo or $33/mo yearly, Hardcore $69/mo or $58/mo yearly, free plan marked Temporarily paused.",
    },
    {
      date: "2026-10-25",
      text: `Crayo prices updated to the October 25, 2026 ladder: Hobby is ${crayoPlanTerms(hobby)}, with quarterly the default term at checkout. Verdicts and the corrections box re-checked at the new prices; competitor prices unchanged from the October 8 to 9 check.`,
    },
  ],
  relatedSpokes: ["crayo-vs-revid", "crayo-vs-storyshort", "crayo-vs-faceless-video"],
  featurePath: "/features/auto-clip",
  ctaHref: "https://crayo.ai/create/auto-clip",
  ctaLabel: "Clip a video now",
  targetQueries: [
    "autoshorts alternative",
    "autoshorts ai vs crayo",
    "crayo vs autoshorts",
    "autoshorts pricing",
    "autoshorts free plan",
    "crayo",
  ],
};
